import { cardValue } from './overtakeCards';
import { SIDES, cardsOf } from './overtakeDeck';

/**
 * What a pot is worth, and who won the match (SPEC-011 PR-11.1).
 *
 * The counterpart of `duelScoring` for the other game, and deliberately much
 * smaller than it: a duel pays for being right and for being quick, so it needs
 * a formula, whereas a pot is simply the cards in it. What earns this a module
 * of its own is the BONUS — a doubled round pays twice, and the second half of
 * that payment cannot be represented as a card because there is no card for it
 * (SPEC-011 D-10). So a pot is a list plus a number, and every screen that wants
 * a score has to add the two together the same way.
 *
 * Pure.
 */

/**
 * What this list of cards is worth.
 *
 * Cards the deck cannot resolve are worth nothing rather than the common's one
 * point, which is the opposite of what `cardValue` does for a car with a missing
 * rarity — and the two are not in conflict. `cardValue` is asked about a card
 * that is definitely in play and prices the unknown at the floor so it cannot be
 * a free discard; this is asked about an id that resolved to no car at all,
 * which is a hole rather than a cheap card, and paying for it would let a
 * malformed match state invent points.
 */
export function potValue(deck, ids) {
    return cardsOf(deck, ids).reduce((total, car) => total + cardValue(car), 0);
}

/** One side's score: everything in its pot, plus what doubling paid it. */
export function sideScore(match, deck, side) {
    return potValue(deck, match?.pots?.[side]) + (match?.bonuses?.[side] ?? 0);
}

/** Both scores, which is what every screen actually wants. */
export function matchTotals(match, deck) {
    return Object.fromEntries(SIDES.map((side) => [side, sideScore(match, deck, side)]));
}

/**
 * Who won, and by how much.
 *
 * `winner` is null on a draw, and a draw is a real outcome rather than a
 * tie-break waiting to be invented: both sides played the same number of cards
 * out of one shared deck, so equal pots is a result the game can genuinely
 * produce. Breaking it on card count or on who led first would be a rule nobody
 * was told about, decided by the module that noticed it was possible.
 */
export function matchOutcome(match, deck) {
    const totals = matchTotals(match, deck);

    if (totals.player === totals.opponent) {
        return { winner: null, totals, margin: 0 };
    }

    const winner = totals.player > totals.opponent ? 'player' : 'opponent';

    return { winner, totals, margin: Math.abs(totals.player - totals.opponent) };
}

/**
 * The most valuable card in a hand — what a lost double surrenders.
 *
 * Ties are broken by position rather than left to the sort, so the card given up
 * is reproducible from the same hand every time. `Array.prototype.sort` is
 * required to be stable in every engine this ships to, but relying on that for a
 * RULE means the rule is a property of the sort implementation; reading it as a
 * fold makes it a property of this function.
 */
export function dearestCard(deck, ids) {
    return cardsOf(deck, ids).reduce(
        (dearest, car) => (dearest && cardValue(dearest) >= cardValue(car) ? dearest : car),
        null,
    );
}
